<template>
  <transition name="overlay-fade">
    <div
      v-if="isVisible"
      class="fixed inset-0 z-[1000] w-full h-screen bg-white dark:bg-background flex flex-col overflow-hidden font-inter text-foreground"
    >
      <!-- New V2 Header: Logo, Breadcrumbs, Action Toolbar, Exit -->
      <header
        class="w-full flex items-center justify-between px-4 lg:px-6 py- bg-white dark:bg-card sh z-[100]"
      >
        <!-- Left side: Logo and Breadcrumbs -->
        <div class="flex items-center gap-4 min-w-0">
          <router-link to="/home" class="flex-shrink-0">
            <img src="/Logo-PG.svg" alt="Logo" class="h-8 w-auto" />
          </router-link>

          <div
            class="flex items-center text-[13px] lg:text-[14px] font-medium text-foreground truncate"
          >
            <span class="truncate max-w-[150px] lg:max-w-[300px]">{{
              goal?.topic || "Learning Goal"
            }}</span>
            <span class="mx-2 text-muted-foreground font-light">|</span>
            <span class="truncate max-w-[120px] lg:max-w-[250px]">{{
              selectedModuleTitle
            }}</span>
            <span class="mx-2 text-muted-foreground font-light">|</span>
            <span
              class="truncate max-w-[150px] lg:max-w-[350px] italic text-muted-foreground"
              >{{ selectedTopic?.name || "Select Topic" }}</span
            >
          </div>
        </div>

        <!-- Right side: Toolbox and Exit -->
        <div class="flex items-center gap-2 lg:gap-4">
          <ActionToolbar
            :showNotes="showNotes"
            :showInfo="showSummary"
            :showModules="showModulesOverlay"
            @add-note="handleAddNoteClick"
            @toggle-notes="toggleNotes"
            @toggle-modules="toggleModules"
            @toggle-info="handleInfoClick"
            @toggle-users="handleUsersClick"
          />

          <button
            @click="goBack"
            class="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-muted transition-colors focus:outline-none"
            title="Exit Lock-In Mode"
          >
            <X class="w-5 h-5 lg:w-6 lg:h-6 text-muted-foreground" />
          </button>
        </div>
      </header>

      <!-- Main Content Area -->
      <div
        class="flex-1 flex overflow-hidden p-2 lg:py-2 lg:px-6 gap-2 lg:gap- relative"
      >
        <!-- Mobile Backdrop Overlay -->
        <div
          v-if="isAnySidebarVisible && isMobile"
          class="fixed inset-0 bg-background/80 backdrop-blur-sm z-[50] animate-in fade-in duration-300"
          @click="closeAllSidebars"
        ></div>

        <!-- Center Panel: Content Viewer -->
        <main
          class="flex-1 flex p-2 lg:p-0 flex-col bg-white dark:bg-card rounded-[20px] shadow-sm border-[5px] border-border overflow-hidden relative"
        >
          <div
            class="flex-1 flex items-center justify-center bg-muted/20 overflow-hidden relative"
          >
            <!-- 1. Video View (Highest Priority) -->
            <iframe
              v-if="currentVideoUrl"
              :src="currentVideoUrl"
              class="w-full h-full absolute inset-0"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>

            <!-- 2. Practice Task View -->
            <div
              v-else-if="showPracticeTask && selectedTopic?.practice_task"
              class="w-full h-full overflow-y-auto bg-white dark:bg-card p-8 lg:p-16 custom-scrollbar"
            >
              <!-- Practice Task Content -->
              <div
                class="max-w-4xl space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700"
              >
                <div class="space-y-4">
                  <h1
                    class="text-4xl font-extrabold text-foreground tracking-tight"
                  >
                    Practice Task:
                    {{
                      selectedTopic.practice_task.title || selectedTopic.name
                    }}
                  </h1>
                  <p
                    class="text-[17px] text-foreground/70 leading-relaxed max-w-3xl"
                  >
                    {{
                      selectedTopic.practice_task.context ||
                      selectedTopic.description
                    }}
                  </p>
                </div>

                <div class="space-y-10">
                  <div class="space-y-4">
                    <h3 class="text-[18px] font-bold text-foreground">
                      Requirements
                    </h3>
                    <ul class="space-y-2 ml-1">
                      <li
                        v-for="(criteria, idx) in selectedTopic.practice_task
                          .acceptance_criteria ||
                        selectedTopic.practice_task.instructions ||
                        []"
                        :key="idx"
                        class="flex items-start gap-3 text-[15px] text-foreground/80 leading-relaxed"
                      >
                        <span
                          class="mt-2 w-1.5 h-1.5 rounded-full bg-foreground shrink-0"
                        ></span>
                        {{ criteria }}
                      </li>
                    </ul>
                  </div>
                  <div
                    v-if="selectedTopic.practice_task.example"
                    class="space-y-4"
                  >
                    <h3 class="text-[18px] font-bold text-foreground">
                      Example
                    </h3>
                    <div class="space-y-1 ml-1">
                      <div
                        v-for="(
                          line, idx
                        ) in selectedTopic.practice_task.example.split('\n')"
                        :key="idx"
                        class="flex items-start gap-3 text-[15px] text-foreground/80 leading-relaxed"
                      >
                        <span
                          class="mt-2 w-1.5 h-1.5 rounded-full bg-foreground shrink-0"
                        ></span>
                        {{ line }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="space-y-8 pt-4">
                  <h3 class="text-[20px] font-bold text-foreground">
                    Task Submission
                  </h3>

                  <!-- Submitted AI Feedback Card -->
                  <div
                    v-if="selectedTopic.practice_submission"
                    class="bg-foreground/5 border border-border rounded-3xl p-8 space-y-4 my-8 animate-in fade-in duration-500 shadow-sm"
                  >
                    <div class="flex items-center gap-3.5">
                      <div
                        class="w-10 h-10 rounded-full bg-foreground text-background flex items-center justify-center font-extrabold text-[15px] shadow-sm"
                      >
                        AI
                      </div>
                      <h4
                        class="text-[18px] font-extrabold text-foreground tracking-tight"
                      >
                        AI Feedback
                      </h4>
                      <span
                        class="ml-auto px-4 py-1.5 bg-green-500/10 text-green-600 dark:text-green-400 rounded-full text-[12px] font-extrabold uppercase tracking-wider flex items-center gap-1.5 border border-green-500/20"
                      >
                        <CheckCircle class="w-3.5 h-3.5" /> Completed
                      </span>
                    </div>
                    <p
                      class="text-[16px] text-foreground/85 leading-relaxed pt-2 font-medium"
                    >
                      {{
                        selectedTopic.practice_submission.ai_evaluation
                          ?.ai_note ||
                        "Great job completing this practice task!"
                      }}
                    </p>
                    <div
                      class="pt-4 flex flex-wrap gap-6 text-[14px] text-muted-foreground border-t border-border/60 mt-4 items-center justify-between"
                    >
                      <div class="flex flex-wrap gap-6">
                        <span
                          v-if="
                            selectedTopic.practice_submission.submission_url
                          "
                          class="flex items-center gap-2"
                        >
                          <LinkIcon class="w-4 h-4 text-foreground/60" />
                          Submitted Link:
                          <a
                            :href="
                              selectedTopic.practice_submission.submission_url
                            "
                            target="_blank"
                            class="text-foreground font-bold hover:underline truncate max-w-xs"
                            >{{
                              selectedTopic.practice_submission.submission_url
                            }}</a
                          >
                        </span>
                        <span
                          v-if="
                            selectedTopic.practice_submission
                              .submission_file_url
                          "
                          class="flex items-center gap-2"
                        >
                          <FileText class="w-4 h-4 text-foreground/60" />
                          Submitted File:
                          <a
                            :href="
                              selectedTopic.practice_submission
                                .submission_file_url
                            "
                            target="_blank"
                            class="text-foreground font-bold hover:underline"
                            >Download / View</a
                          >
                        </span>
                      </div>
                      <button
                        @click="resetSubmission"
                        class="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground text-[13px] font-bold rounded-xl transition-colors"
                      >
                        Resubmit Task
                      </button>
                    </div>
                  </div>

                  <!-- Submission Form (Unsubmitted) -->
                  <div v-else class="space-y-8">
                    <div
                      class="group border-[1.5px] border-dashed border-border rounded-3xl p-16 flex flex-col items-center justify-center gap-4 hover:border-foreground/30 hover:bg-muted/10 transition-all cursor-pointer relative overflow-hidden"
                      @click="$refs.fileInput.click()"
                    >
                      <input
                        type="file"
                        ref="fileInput"
                        class="hidden"
                        @change="handleFileChange"
                      />
                      <div
                        class="w-12 h-12 rounded-full bg-muted/40 flex items-center justify-center group-hover:scale-110 transition-transform"
                      >
                        <CloudUpload class="w-6 h-6 text-foreground/50" />
                      </div>
                      <div class="text-center space-y-1">
                        <p class="text-[15px] font-bold text-foreground">
                          {{
                            submissionFile
                              ? submissionFile.name
                              : "Click to upload or drag and drop"
                          }}
                        </p>
                        <p class="text-[13px] text-muted-foreground/60">
                          Max file size: 5MB. Supported formats: .PDF, .DOCX,
                          .TXT, .MD, .PY, .JS, .CSV, etc.
                        </p>
                      </div>
                    </div>
                    <div class="space-y-6 max-w-2xl">
                      <div class="space-y-4">
                        <p
                          class="text-[14px] font-bold text-muted-foreground/80"
                        >
                          Or if link, submit here:
                        </p>
                        <div class="space-y-2">
                          <label
                            class="text-[13px] font-extrabold text-foreground uppercase tracking-widest"
                            >URL</label
                          >
                          <input
                            v-model="submissionUrl"
                            type="text"
                            placeholder="https://github.com/your-project"
                            class="w-full bg-transparent border border-border rounded-2xl px-6 py-4 text-[15px] focus:outline-none focus:ring-1 focus:ring-foreground/20 transition-all placeholder:text-muted-foreground/30"
                          />
                        </div>
                      </div>
                      <button
                        @click="submitPracticeTask"
                        class="px-12 py-3.5 bg-[#2D3139] text-white rounded-full font-bold text-[15px] hover:bg-[#1a1d22] transition-all shadow-md active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3"
                        :disabled="submittingTask"
                      >
                        <Loader
                          v-if="submittingTask"
                          class="w-4 h-4 animate-spin"
                        />
                        {{ submittingTask ? "Submitting..." : "Submit task" }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 3. Specific Paper View (Prioritized over Lesson Body) -->
            <div
              v-else-if="
                currentPaperIndex !== null &&
                selectedTopic?.resources?.papers?.[currentPaperIndex]
              "
              class="w-full h-full overflow-y-auto bg-white dark:bg-card p-6 lg:p-12 custom-scrollbar"
            >
              <div
                class="max-w-4xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700"
              >
                <div class="space-y-4 border-b border-border pb-8">
                  <div
                    class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/20 text-secondary-foreground text-xs font-bold uppercase tracking-wider"
                  >
                    Reading Material
                  </div>
                  <h2
                    class="text-2xl lg:text-3xl font-bold text-foreground tracking-tight"
                  >
                    {{ selectedTopic.name }}
                  </h2>
                </div>

                <div
                  class="p-8 rounded-3xl border-2 border-primary/20 bg-card shadow-xl"
                >
                  <div class="space-y-6">
                    <div class="flex items-start justify-between gap-6">
                      <h3
                        class="text-3xl font-extrabold text-foreground tracking-tight leading-tight"
                      >
                        {{
                          selectedTopic.resources.papers[currentPaperIndex]
                            .title
                        }}
                      </h3>
                      <div class="flex gap-3 shrink-0">
                        <a
                          v-if="
                            selectedTopic.resources.papers[currentPaperIndex]
                              .pdf_url
                          "
                          :href="
                            selectedTopic.resources.papers[currentPaperIndex]
                              .pdf_url
                          "
                          target="_blank"
                          class="w-12 h-12 flex items-center justify-center rounded-2xl bg-primary text-white hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                        >
                          <FileDown class="w-6 h-6" />
                        </a>
                      </div>
                    </div>
                    <div
                      v-if="
                        selectedTopic.resources.papers[currentPaperIndex]
                          .authors?.length
                      "
                      class="text-sm text-muted-foreground font-medium"
                    >
                      By
                      {{
                        Array.isArray(
                          selectedTopic.resources.papers[currentPaperIndex]
                            .authors,
                        )
                          ? selectedTopic.resources.papers[
                              currentPaperIndex
                            ].authors.join(", ")
                          : selectedTopic.resources.papers[currentPaperIndex]
                              .authors
                      }}
                    </div>
                    <div
                      class="prose dark:prose-invert max-w-none bg-muted/30 p-8 rounded-2xl border border-border"
                    >
                      <h4
                        class="text-lg font-bold mb-4 flex items-center gap-2"
                      >
                        <BookOpen class="w-5 h-5 text-primary" />Summary
                      </h4>
                      <p
                        class="text-[17px] text-foreground/80 leading-relaxed italic"
                        \
                      >
                        {{
                          selectedTopic.resources.papers[currentPaperIndex]
                            .summary ||
                          selectedTopic.resources.papers[currentPaperIndex]
                            .abstract
                        }}
                      </p>
                    </div>
                    <div
                      class="flex items-center justify-between pt-6 border-t border-border"
                    >
                      <button
                        @click="currentPaperIndex = null"
                        class="text-sm font-bold text-primary hover:underline flex items-center gap-2"
                      >
                        Back to all materials
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- 4. Lesson Body (Default Reading Material) -->
            <div
              v-else-if="selectedTopic?.lesson_body"
              class="w-full h-full overflow-y-auto bg-white dark:bg-card p-6 lg:p-12 custom-scrollbar"
            >
              <div
                class="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700"
              >
                <div class="space-y-4 border-b border-border pb-8">
                  <div
                    class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider"
                  >
                    Personalized Lesson
                  </div>
                  <h2
                    class="text-2xl lg:text-3xl font-bold text-foreground tracking-tight"
                  >
                    {{ selectedTopic.name }}
                  </h2>
                  <p
                    class="text-base lg:text-[16px] text-muted-foreground/70 leading-relaxed"
                  >
                    {{ selectedTopic.description }}
                  </p>
                </div>
                <div
                  class="prose dark:prose-invert prose-headings:text-foreground prose-p:text-foreground/80 max-w-none text-base leading-relaxed"
                  v-html="renderMarkdown(selectedTopic.lesson_body)"
                ></div>
              </div>
            </div>

            <!-- 5. General Papers List (If no specific paper or lesson selected) -->
            <div
              v-else-if="selectedTopic?.resources?.papers?.length"
              class="w-full h-full overflow-y-auto bg-white dark:bg-card p-6 lg:p-12 custom-scrollbar"
            >
              <div
                class="max-w-4xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700"
              >
                <div class="space-y-4 border-b border-border pb-8">
                  <div
                    class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/20 text-secondary-foreground text-xs font-bold uppercase tracking-wider"
                  >
                    Reading Materials
                  </div>
                  <h2
                    class="text-2xl lg:text-3xl font-bold text-foreground tracking-tight"
                  >
                    {{ selectedTopic.name }}
                  </h2>
                  <p
                    class="text-base lg:text-[16px] text-muted-foreground/70 leading-relaxed"
                  >
                    {{ selectedTopic.description }}
                  </p>
                </div>

                <div class="space-y-8">
                  <div
                    v-for="(paper, index) in selectedTopic.resources.papers"
                    :key="index"
                    class="group p-6 rounded-2xl border border-border bg-card/50 hover:bg-card hover:border-primary/30 transition-all duration-300 cursor-pointer"
                    @click="currentPaperIndex = index"
                  >
                    <div class="space-y-4">
                      <div class="flex items-start justify-between gap-4">
                        <h3
                          class="text-xl font-bold text-foreground group-hover:text-primary transition-colors leading-snug"
                        >
                          {{ paper.title }}
                        </h3>
                        <div
                          class="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 text-primary"
                        >
                          <BookOpen class="w-5 h-5" />
                        </div>
                      </div>
                      <div
                        v-if="paper.authors?.length"
                        class="text-sm text-muted-foreground font-medium"
                      >
                        By
                        {{
                          Array.isArray(paper.authors)
                            ? paper.authors.join(", ")
                            : paper.authors
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center p-6">
              <p
                class="text-muted-foreground underline cursor-pointer hover:text-foreground transition-colors"
                @click="toggleModules"
              >
                Select a video from the modules to watch or explore reading
                materials
              </p>
            </div>
          </div>
        </main>

        <!-- Sidebars container -->
        <div
          class="h-full flex gap-6 shrink-0 transition-all duration-300"
          :class="[
            isMobile
              ? 'fixed top-20 right-0 p-4 w-full max-w-[500px] pointer-events-none z-[60]'
              : 'relative z-[40]',
          ]"
        >
          <NotesSidebar
            ref="notesSidebar"
            :isVisible="showNotes"
            :notes="notes"
            class="pointer-events-auto h-full"
            :class="{ 'shadow-2xl': isMobile }"
            @close="showNotes = false"
            @save-note="handleSaveNote"
            @delete-note="handleDeleteNote"
            @toggle-pin="handleTogglePin"
          />

          <Summary
            :isVisible="showSummary"
            :goal="goal"
            :modules="KeyTakeaways"
            :selectedTopic="selectedTopic"
            class="pointer-events-auto h-full"
            :class="{ 'shadow-2xl': isMobile }"
            @close="showSummary = false"
          />

          <ModulesSidebar
            :isVisible="showModulesOverlay"
            :modules="learningModules"
            :selectedTopic="selectedTopic"
            :currentVideoUrl="currentVideoUrl"
            :activeTopicId="activeTopicId"
            class="pointer-events-auto h-full"
            :class="{ 'shadow-2xl': isMobile }"
            @close="showModulesOverlay = false"
            @select-topic="handleTopicSelection"
            @play-video="handlePlayVideo"
            @play-text="handlePlayText"
            @play-paper="handlePlayPaper"
            @play-practice-task="handlePlayPracticeTask"
            @toggle-completion="handleToggleCompletion"
          />
        </div>
      </div>
    </div>
  </transition>

  <DeleteConfirmationModal
    :isOpen="!!noteToDelete"
    @close="noteToDelete = null"
    @confirm="confirmDeleteNote"
  />
</template>

<script>
import apiClient from "@/axios";
import {
  X,
  FileDown,
  BookOpen,
  CloudUpload,
  Loader,
  CheckCircle,
  Link as LinkIcon,
  FileText,
} from "lucide-vue-next";
import { marked } from "marked";
import DOMPurify from "dompurify";
import ActionToolbar from "./ActionToolbar.vue";
import ModulesSidebar from "./ModulesSidebar.vue";
import NotesSidebar from "./NotesSidebar.vue";
import Summary from "./Summary.vue";
import DeleteConfirmationModal from "@/components/modals/DeleteConfirmationModal.vue";

export default {
  name: "LockInModeComponent",
  components: {
    X,
    FileDown,
    BookOpen,
    CloudUpload,
    Loader,
    CheckCircle,
    LinkIcon,
    FileText,
    ActionToolbar,
    ModulesSidebar,
    NotesSidebar,
    Summary,
    DeleteConfirmationModal,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: true, // Keep true for backwards compatibility if still used as a route
    },
    goalData: {
      type: Object,
      default: null,
    },
    initialTaskId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      showNotes: false,
      showSummary: false,
      showModulesOverlay: false,
      goal: null,
      loading: true,
      notes: [],
      selectedTopic: null,
      isMobile: false,
      currentVideoUrl: null,
      activeTopicId: null,
      noteToDelete: null,
      currentPaperIndex: null,
      showPracticeTask: false,
      submissionUrl: "",
      submissionFile: null,
      submittingTask: false,
    };
  },
  computed: {
    isAnySidebarVisible() {
      return this.showNotes || this.showSummary || this.showModulesOverlay;
    },
    projectId() {
      return this.$route.params.id;
    },
    userId() {
      const user = this.$store.getters.currentUser;
      return user?.id || user?.user_id;
    },
    KeyTakeaways() {
      return Array.isArray(this.goal?.learning_tree?.modules)
        ? this.goal.learning_tree.modules
        : [];
    },
    selectedModuleTitle() {
      if (!this.selectedTopic || !this.learningModules.length) return "Module";
      const taskId = this.selectedTopic.task_id || this.selectedTopic.id;
      for (const module of this.learningModules) {
        if (module.topics?.some((t) => (t.task_id || t.id) === taskId)) {
          return module.name;
        }
      }
      return "Module";
    },
    learningModules() {
      if (Array.isArray(this.goal?.tasks)) {
        return this.goal.tasks
          .filter(task => (!task.parent_task_id) && task.learning_task_type !== 'project')
          .map((task) => ({
          ...task,
          name: task.title,
          topics:
            task.sub_tasks?.map((sub) => {
              // Find the internal text resource if lesson_body is missing
              const internalRes = sub.external_resources?.find(
                (r) => r.type === "text" || r.type === "internal",
              );
              return {
                ...sub,
                name: sub.title,
                lesson_body:
                  sub.lesson_body ||
                  internalRes?.content ||
                  internalRes?.description ||
                  "",
                resources: {
                  videos:
                    sub.external_resources
                      ?.filter((r) => r.type === "video")
                      .map((r) => ({ ...r, video_url: r.url })) || [],
                  internal_resources:
                    sub.external_resources
                      ?.filter(
                        (r) => r.type === "text" || r.type === "internal",
                      )
                      .map((r) => ({
                        ...r,
                        content: r.content || r.description || "",
                      })) || [],
                  papers:
                    sub.external_resources
                      ?.filter(
                        (r) =>
                          r.type === "academic_paper" ||
                          r.PDF_url ||
                          r.pdf_url ||
                          r.abstract ||
                          r.summary ||
                          r.authors,
                      )
                      .map((r) => ({ ...r })) || [],
                },
              };
            }) || [],
        }));
      }
      // Fallback or empty if tasks not present
      return [];
    },
  },
  created() {
    this.loadState();
  },
  mounted() {
    if (this.isVisible) {
      if (this.goalData) {
        this.goal = this.goalData;
        this.syncInitialState();
        this.loading = false;
      } else {
        this.fetchGoal();
      }
      this.fetchNotes();
      this.checkMobile();
    }
    window.addEventListener("resize", this.checkMobile);
  },
  watch: {
    isVisible(val) {
      if (val) {
        if (this.goalData) {
          this.goal = this.goalData;
          this.syncInitialState();
          this.loading = false;
        } else {
          this.fetchGoal();
        }
        this.fetchNotes();
        this.checkMobile();
      }
    },
    initialTaskId() {
      if (this.isVisible) {
        this.syncInitialState();
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkMobile);
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth < 1024; // Align with lg breakpoint
    },
    closeAllSidebars() {
      this.showNotes = false;
      this.showSummary = false;
      this.showModulesOverlay = false;
    },
    async fetchGoal() {
      try {
        this.loading = true;
        const response = await apiClient.get(
          `/web/learning-goals/${this.projectId}`,
        );
        this.goal = response.data || null;

        this.syncInitialState();
      } catch (error) {
        console.error("Error fetching learning goal:", error);
      } finally {
        this.loading = false;
      }
    },

    syncInitialState() {
      if (!this.goal) return;

      // Initialize selectedTopic and check user specific progress
      const savedId = this.loadState();
      const queryId = this.$route.query.taskId;
      const targetId = this.initialTaskId || queryId || savedId;

      let foundTarget = null;

      if (targetId && this.learningModules.length > 0) {
        // Try to find the targeted topic
        for (const module of this.learningModules) {
          if (module.topics) {
            const found = module.topics.find(
              (t) => t.task_id == targetId || t.id == targetId,
            );
            if (found) {
              foundTarget = found;
              break;
            }
          }
        }
      }

      if (foundTarget) {
        this.selectedTopic = foundTarget;
        // Auto-play the first video if it exists and we're coming from a query or initialTaskId
        if (
          (queryId || this.initialTaskId) &&
          this.selectedTopic.resources?.videos?.length > 0
        ) {
          this.handlePlayVideo({
            url: this.selectedTopic.resources.videos[0].video_url,
            topicId: this.selectedTopic.task_id || this.selectedTopic.id,
          });
        }
      } else if (this.learningModules.length > 0) {
        // Default to first topic
        const firstModule = this.learningModules[0];
        if (
          Array.isArray(firstModule.topics) &&
          firstModule.topics.length > 0 &&
          !this.selectedTopic
        ) {
          this.selectedTopic = firstModule.topics[0];
        }
      }
    },

    async fetchNotes() {
      if (!this.projectId) return;
      try {
        const response = await apiClient.get(
          `/web/notes/${this.projectId}/all`,
          {
            params: {
              page: 1,
              page_size: 50,
              pinned_first: true,
            },
          },
        );
        this.notes = response.data.data?.notes || [];
      } catch (error) {
        console.error("Failed to fetch notes:", error);
        this.notes = [];
      }
    },
    goBack() {
      this.$emit("close");
      // If used as a standalone route, also go back
      if (this.$route.name === "LockInMode") {
        this.$router.back();
      }
    },
    toggleNotes() {
      if (!this.showNotes) {
        // Opening the sidebar
        this.showNotes = true;
        this.showSummary = false;
        this.showModulesOverlay = false;
        // Ensure it shows the list when opening from the notes icon
        this.$nextTick(() => {
          if (this.$refs.notesSidebar) {
            this.$refs.notesSidebar.cancelCreate();
          }
        });
      } else {
        // If already open, check if it's in creation mode
        if (this.$refs.notesSidebar && this.$refs.notesSidebar.isCreating) {
          // Switch to list mode
          this.$refs.notesSidebar.cancelCreate();
        } else {
          // Already in list mode, so close it (toggle behavior)
          this.showNotes = false;
        }
      }
      this.saveState();
    },
    toggleModules() {
      this.showModulesOverlay = !this.showModulesOverlay;
      if (this.showModulesOverlay) {
        this.showNotes = false;
        this.showSummary = false;
      }
      this.saveState();
    },
    toggleSummary() {
      this.showSummary = !this.showSummary;
      if (this.showSummary) {
        this.showNotes = false;
        this.showModulesOverlay = false;
      }
      this.saveState();
    },
    saveState() {
      if (!this.projectId || !this.userId) return;

      const state = {
        selectedTopicId: this.selectedTopic?.task_id || this.selectedTopic?.id,
        currentVideoUrl: this.currentVideoUrl,
        activeTopicId: this.activeTopicId,
        showNotes: this.showNotes,
        showSummary: this.showSummary,
        showModulesOverlay: this.showModulesOverlay,
      };

      localStorage.setItem(
        `lockin_state_${this.userId}_${this.projectId}`,
        JSON.stringify(state),
      );
    },
    loadState() {
      if (!this.projectId || !this.userId) {
        // Fallback if user/project not ready (unlikely)
        if (!this.showNotes && !this.showSummary && !this.showModulesOverlay) {
          this.showModulesOverlay = true;
        }
        return null;
      }

      const saved = localStorage.getItem(
        `lockin_state_${this.userId}_${this.projectId}`,
      );

      const forceModules = !!this.initialTaskId || !!this.$route.query.taskId;

      if (saved) {
        try {
          const state = JSON.parse(saved);
          this.currentVideoUrl = state.currentVideoUrl;
          this.activeTopicId = state.activeTopicId;

          if (forceModules) {
            this.showModulesOverlay = true;
            this.showNotes = false;
            this.showSummary = false;
          } else {
            this.showNotes = state.showNotes;
            this.showSummary = state.showSummary; // Restore summary visibility
            this.showModulesOverlay = state.showModulesOverlay;
          }
          return state.selectedTopicId;
        } catch (e) {
          console.error("Failed to parse saved state", e);
          if (forceModules) {
            this.showModulesOverlay = true;
          }
        }
      } else {
        // Fallback: Default to Modules open if no state saved
        this.showModulesOverlay = true;
      }
      return null;
    },
    handleTopicSelection(topic) {
      this.selectedTopic = topic;
      this.currentPaperIndex = null; // Reset selection when topic changes
      this.showPracticeTask = false; // Reset practice task view
      // Only close modules overlay on mobile to reveal content
      if (this.isMobile) {
        this.showModulesOverlay = false;
      }
      // Do not automatically switch to summary sidebar on desktop
      this.saveState();
    },
    handlePlayVideo(data) {
      if (!data) return;
      const url = typeof data === "string" ? data : data.url;
      const topicId = typeof data === "string" ? null : data.topicId;

      if (url) {
        if (topicId) this.updateSelectedTopicById(topicId);
        this.currentVideoUrl = this.getEmbedUrl(url);
        this.currentPaperIndex = null;
        this.showPracticeTask = false;
        if (topicId) this.activeTopicId = topicId;

        // Ensure sidebar closes on mobile if needed
        if (this.isMobile) {
          this.closeAllSidebars();
        }
        this.saveState();
      }
    },
    handlePlayText(data) {
      if (!data) return;
      const content = data.content;
      const topicId = data.topicId;

      if (content) {
        if (topicId) this.updateSelectedTopicById(topicId);
        // Clear video so text view shows
        this.currentVideoUrl = null;
        this.currentPaperIndex = null;
        this.showPracticeTask = false;
        if (topicId) this.activeTopicId = topicId;

        // Find the topic to ensure its lesson_body is set for the view
        if (
          this.selectedTopic &&
          (this.selectedTopic.task_id === topicId ||
            this.selectedTopic.id === topicId)
        ) {
          this.selectedTopic.lesson_body = content;
        }

        if (this.isMobile) {
          this.closeAllSidebars();
        }
        this.saveState();
      }
    },
    handlePlayPaper(data) {
      if (!data) return;
      const index = data.paperIndex;
      const topicId = data.topicId;

      if (index !== undefined) {
        if (topicId) this.updateSelectedTopicById(topicId);
        // Clear video so paper view shows
        this.currentVideoUrl = null;
        this.currentPaperIndex = index;
        this.showPracticeTask = false;
        if (topicId) this.activeTopicId = topicId;

        if (this.isMobile) {
          this.closeAllSidebars();
        }
        this.saveState();
      }
    },
    getEmbedUrl(url) {
      if (!url) return null;
      // Handle standard YouTube URLs
      let videoId = null;
      if (url.includes("youtube.com/watch?v=")) {
        videoId = url.split("v=")[1].split("&")[0];
      } else if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1];
      }

      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
      }
      return url;
    },
    handlePlayPracticeTask(data) {
      if (!data) return;
      const topicId = data.topicId;

      if (topicId) {
        this.updateSelectedTopicById(topicId);
        this.currentVideoUrl = null;
        this.currentPaperIndex = null;
        this.showPracticeTask = true;
        this.activeTopicId = topicId;

        if (this.isMobile) {
          this.closeAllSidebars();
        }
        this.saveState();
      }
    },
    updateSelectedTopicById(topicId) {
      if (!topicId) return;
      // Search through learningModules to find the topic with matching ID
      for (const module of this.learningModules) {
        const topic = module.topics.find(
          (t) => t.task_id === topicId || t.id === topicId,
        );
        if (topic) {
          this.selectedTopic = topic;
          break;
        }
      }
    },
    handleFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.submissionFile = file;
      }
    },
    async submitPracticeTask() {
      if (!this.submissionUrl && !this.submissionFile) {
        this.$toast?.error("Error", "Please provide a link or upload a file.");
        return;
      }

      try {
        this.submittingTask = true;
        let fileUrl = null;
        let extractedText = null;

        if (this.submissionFile) {
          const uploadData = new FormData();
          uploadData.append("file", this.submissionFile);
          const uploadRes = await apiClient.post(
            "/web/learning-goals/upload-resource",
            uploadData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            },
          );
          if (uploadRes.data?.success) {
            fileUrl = uploadRes.data.url;
            extractedText = uploadRes.data.full_text;
          } else {
            this.$toast?.error(
              "Error",
              uploadRes.data?.message || "Failed to upload file.",
            );
            return;
          }
        }

        const taskId = this.selectedTopic.task_id || this.selectedTopic.id;
        const response = await apiClient.post(
          `/web/learning-goals/${this.projectId}/tasks/${taskId}/submit-practice`,
          {
            submission_url: this.submissionUrl || null,
            submission_file_url: fileUrl || null,
            extracted_text: extractedText || null,
            notes: "",
          },
        );

        this.$toast?.success(
          "Success",
          "Practice task completed! Check out your AI Tutor's feedback.",
        );

        // Reset state
        this.submissionUrl = "";
        this.submissionFile = null;

        // Update local state reactivity
        if (this.selectedTopic) {
          this.selectedTopic.practice_submission =
            response.data.practice_submission;
          this.selectedTopic.task_status = "Completed";
          this.handleToggleCompletion(this.selectedTopic);
        }
      } catch (error) {
        console.error("Error submitting task:", error);
        const errorMsg =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to submit task. Please try again.";
        this.$toast?.error("Error", errorMsg);
      } finally {
        this.submittingTask = false;
      }
    },
    resetSubmission() {
      if (this.selectedTopic) {
        this.selectedTopic.practice_submission = null;
        this.selectedTopic.task_status = "In Progress";
        this.submissionUrl = "";
        this.submissionFile = null;
        this.handleToggleCompletion(this.selectedTopic);
      }
    },
    async handleToggleCompletion(topic) {
      if (!topic || !this.projectId) return;

      const taskId = topic.task_id || topic.id;
      if (!taskId) return;

      // Find the task in the source data (this.goal.tasks) to ensure reactivity triggers properly
      let foundSubTask = null;
      if (this.goal && this.goal.tasks) {
        for (const parentTask of this.goal.tasks) {
          if (parentTask.sub_tasks) {
            const found = parentTask.sub_tasks.find(
              (st) => st.task_id === taskId || st.id === taskId,
            );
            if (found) {
              foundSubTask = found;
              break;
            }
          }
        }
      }

      // If not found in source (shouldn't happen), use the passed topic
      const targetTask = foundSubTask || topic;

      // Calculate new state
      const wasCompleted = this.isTopicCompleted(targetTask);
      const newCompletedState = !wasCompleted;
      const newStatus = newCompletedState ? "Completed" : "In Progress";

      // Optimistic update
      const originalStatus = targetTask.status;
      const originalCompleted = targetTask.completed;

      targetTask.completed = newCompletedState;
      targetTask.status = newStatus;

      // Also update the passed topic reference directly to ensure immediate UI feedback
      // in case the computed property doesn't re-eval immediately or if they are different references
      if (topic !== targetTask) {
        topic.completed = newCompletedState;
        topic.status = newStatus;
      }

      try {
        const payload = {
          task_status: newStatus,
        };

        await apiClient.put(
          `/web/tasks/${taskId}?project_id=${this.projectId}`,
          payload,
        );
        this.$toast?.success(
          newCompletedState ? "Task Completed" : "Task Updated",
          newCompletedState ? "Great job!" : "Status marked as In Progress.",
        );
      } catch (error) {
        console.error("Failed to update task status:", error);
        // Revert on failure
        targetTask.status = originalStatus;
        targetTask.completed = originalCompleted;
        if (topic !== targetTask) {
          topic.status = originalStatus;
          topic.completed = originalCompleted;
        }
        this.$toast?.error("Error", "Failed to update task status.");
      }
    },
    isTopicCompleted(topic) {
      return (
        topic.status === "Completed" ||
        topic.completed === true ||
        (topic.completed && topic.completed !== "false")
      );
    },
    handleAddNoteClick() {
      this.showNotes = true;
      this.showSummary = false;
      this.showModulesOverlay = false;
      this.saveState();

      // Wait for render then trigger the sidebar method
      this.$nextTick(() => {
        if (this.$refs.notesSidebar) {
          this.$refs.notesSidebar.startCreatingNote();
        }
      });
    },
    async handleSaveNote(noteData) {
      if (!this.projectId) return;
      try {
        if (noteData.id) {
          await apiClient.put(
            `/web/notes/${this.projectId}/note/${noteData.id}`,
            {
              title: noteData.title,
              content: noteData.content,
            },
          );
          this.$toast?.success(
            "Note Updated",
            "Your note has been updated successfully.",
          );
        } else {
          await apiClient.post(`/web/notes/${this.projectId}`, {
            title: noteData.title,
            content: noteData.content,
            is_pinned: false,
          });
          this.$toast?.success(
            "Note Saved",
            "Your note has been saved successfully.",
          );
        }
        await this.fetchNotes();
      } catch (error) {
        console.error("Failed to save/update note:", error);
        this.$toast?.error("Error", "Failed to save your note.");
      }
    },
    handleDeleteNote(note) {
      if (!this.projectId || !note.id) return;
      this.noteToDelete = note;
    },
    async confirmDeleteNote() {
      if (!this.projectId || !this.noteToDelete) return;

      try {
        await apiClient.delete(
          `/web/notes/${this.projectId}/note/${this.noteToDelete.id}`,
        );
        this.$toast?.success("Success", "Note deleted successfully.");
        this.noteToDelete = null;
        await this.fetchNotes();
      } catch (error) {
        console.error("Failed to delete note:", error);
        this.$toast?.error("Error", "Failed to delete the note.");
      }
    },
    async handleTogglePin(note) {
      if (!this.projectId || !note.id) return;

      try {
        await apiClient.put(`/web/notes/${this.projectId}/note/${note.id}/pin`);
        this.$toast?.success(
          "Success",
          note.is_pinned ? "Note unpinned." : "Note pinned.",
        );
        await this.fetchNotes();
      } catch (error) {
        console.error("Failed to toggle pin:", error);
        this.$toast?.error("Error", "Failed to toggle pin.");
      }
    },
    handleInfoClick() {
      this.toggleSummary();
    },
    handleUsersClick() {
      this.$toast?.info("Not Implemented", "View coming soon.");
    },
    renderMarkdown(content) {
      if (!content) return "";

      // 1. Smart Dedent: Remove consistent leading whitespace
      const lines = content.split("\n");

      // Filter out empty lines to find the actual minimum indent
      const nonEmojiLines = lines.filter((line) => line.trim().length > 0);
      if (nonEmojiLines.length > 0) {
        const minIndent = Math.min(
          ...nonEmojiLines.map((line) => line.match(/^ */)[0].length),
        );
        content = lines.map((line) => line.slice(minIndent)).join("\n");
      }

      // 2. Remove redundant first-level header if it's at the start
      // AI often starts with "# Topic Title"
      content = content.replace(/^#\s+.*\n?/, "").trim();

      // Alternative: Remove first line if it's identical or very similar to topic title
      // but the regex above is safer for standard Markdown output

      // 3. Render and Sanitize
      const rawHtml = marked.parse(content);
      return DOMPurify.sanitize(rawHtml);
    },
  },
};
</script>

<style scoped>
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.3s ease;
}
.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}
</style>
